import axiosClient from '../utils/apiClient';
import { getEnvironmentVariable } from '../helper/config';
import { EnvVariables, Sweep } from '../utils/helper.type';
import apiClient from '../utils/apiClient';
import { isApiError } from '../utils/helper';
import { AxiosResponse } from 'axios';
import {
  OpeningHourSchedule,
  Player,
  SweepEditHistory,
  UpdateSweepRequest,
  UpdateSweepResponse,
  UpdateUserResponse,
} from '@inplay/models';

export class SweepService {
  private baseApi: string;
  constructor() {
    const baseUrl: string | undefined = axiosClient.defaults.baseURL;

    if (!baseUrl) {
      throw new Error('baseURL is not defined in axiosClient.');
    }

    this.baseApi = baseUrl;
  }
  public async getSweepSummary(
    startTime: string,
    endTime: string,
    venueBranchIds: string[],
  ): Promise<Sweep[]> {
    try {
      const sweepSummaryApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_GET_SWEEPS,
      );

      const venueBranchIdsArray: string[] = Array.isArray(venueBranchIds)
        ? venueBranchIds
        : [venueBranchIds];

      const queryParams: URLSearchParams = new URLSearchParams({
        startTime,
        endTime,
      });
      venueBranchIdsArray.forEach((id: string) => {
        queryParams.append('venueBranchIds', id);
      });

      const sweepApiEndpoint: string = `${
        this.baseApi
      }${sweepSummaryApiResource}?${queryParams.toString()}`;

      const sweepApiResponse: AxiosResponse =
        await apiClient.get(sweepApiEndpoint);

      console.log(`[sweepService][getSweepSummary]`, {
        sweepApiResponse,
      });

      return sweepApiResponse?.data;
    } catch (error: unknown) {
      console.error(`[UserService][getSweepSummary][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async getVenueBranchOpeningHours(
    venueBranchId: string,
  ): Promise<OpeningHourSchedule> {
    try {
      const venueBrnachOpeningHoursApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_GET_VENUE_OPENING_HOURS,
      );

      const queryParams: URLSearchParams = new URLSearchParams({
        venueBranchId,
      });

      const venueBrnachOpeningHoursApiEndpoint: string = `${
        this.baseApi
      }${venueBrnachOpeningHoursApiResource}?${queryParams.toString()}`;

      const venueBrnachOpeningHoursApiResponse: AxiosResponse =
        await apiClient.get(venueBrnachOpeningHoursApiEndpoint);

      console.log(`[sweepService][getVenueBranchOpeningHours]`, {
        venueBrnachOpeningHoursApiResponse,
      });

      return venueBrnachOpeningHoursApiResponse?.data;
    } catch (error: unknown) {
      console.error(`[sweepService][getVenueBranchOpeningHours][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async updateSweep(
    sweepId: string,
    version: number,
    completedBy?: {
      firstName: string;
      lastName: string;
      _id: string;
      notes?: string;
      signature?: string;
    },
    reviewedBy?: {
      firstName: string;
      lastName: string;
      _id: string;
      notes?: string;
      signature?: string;
    },
    players?: Player[],
  ): Promise<UpdateSweepResponse> {
    try {
      console.log(`[ENTRY][SweepService](updateSweep)...`);

      const payload: UpdateSweepRequest = {
        sweepId,
        ...(completedBy?.signature && {
          completedBy: {
            ...completedBy,
            signature: completedBy.signature,
          },
        }),
        ...(reviewedBy?.signature && {
          reviewedBy: {
            ...reviewedBy,
            signature: reviewedBy.signature,
          },
        }),
        version,
        players: players,
      };

      const updateSweepApiEndpoint: string = `${
        this.baseApi
      }${getEnvironmentVariable(EnvVariables.REACT_APP_SWEEP_UPDATE)}`;

      const updateSweepApiResponse: UpdateSweepResponse = await apiClient.put(
        updateSweepApiEndpoint,
        payload,
      );

      console.log(`[EXIT][SweepService](updateSweep)`, {
        updateSweepApiResponse,
      });

      return updateSweepApiResponse;
    } catch (error) {
      console.error(`[SweepService][updateSweep][ERROR]`, { error });
      if (isApiError(error)) throw error;
      throw error;
    } finally {
      console.log(`[SweepService][updateSweep] finally block`);
    }
  }

  public async approveSweepReport(data: {
    sweepIds?: string[];
    venueBranchId: string;
    startTime?: number;
    endTime?: number;
    reviewedBy: {
      firstName: string;
      lastName: string;
      _id: string;
      notes: string;
      signature: string;
    };
  }): Promise<UpdateUserResponse> {
    try {
      const approveSweepApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_APPROVE_SWEEP_REPORT,
      );

      const approveSweepApiEndpoint: string = `${this.baseApi}${approveSweepApiResource}`;

      const approveSweepApiResponse: AxiosResponse = await apiClient.put(
        approveSweepApiEndpoint,
        data,
      );

      console.log(`[sweepService][approveSweepReport]`, {
        approveSweepApiResponse,
      });

      return approveSweepApiResponse?.data;
    } catch (error: unknown) {
      console.error(`[sweepService][approveSweepReport][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async deleteSweep(sweepIds: string[]): Promise<UpdateSweepResponse> {
    try {
      console.log(`[ENTRY][SweepService](delete sweep)...`, {
        sweepIds,
      });

      const deleteSweepApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_DELETE_SWEEPS_RESOURCE,
      );

      const deleteSweepApiEndpoint: string = `${this.baseApi}${deleteSweepApiResource}`;

      const deleteApiResponse: UpdateSweepResponse = await apiClient.delete(
        deleteSweepApiEndpoint,
        {
          data: { sweepIds },
        },
      );

      console.log(`[EXIT][SweepService](delete sweep)`, {
        deleteApiResponse,
      });
      return deleteApiResponse;
    } catch (error: unknown) {
      console.error(`[SweepService][delete sweep][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getSweepHistory(sweepId: string): Promise<SweepEditHistory> {
    try {
      console.log(`[ENTRY][SweepService](get sweep)...`, {
        sweepId,
      });
      const sweepHistoryApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_GET_SWEEP_HISTORY_RESOURCE,
      );

      const queryParams: URLSearchParams = new URLSearchParams({
        sweepId,
      });

      const sweepHistoryApiEndpoint: string = `${
        this.baseApi
      }${sweepHistoryApiResource}?${queryParams.toString()}`;

      const sweepHistoryApiResponse: AxiosResponse = await apiClient.get(
        sweepHistoryApiEndpoint,
      );

      console.log(`[sweepService][getSweepHistory`, {
        sweepHistoryApiResponse,
      });

      return sweepHistoryApiResponse?.data;
    } catch (error: unknown) {
      console.error(`[sweepService][getSweepHistory][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async downloadSweepReport(
    sweepIds: string[],
  ): Promise<AxiosResponse<string[]>> {
    try {
      console.log(`[SweepService][download sweep report][ENTRY]`, { sweepIds });

      const downloadSweepApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_DOWNLOAD_SWEEPS_RESOURCE,
      );
      const downloadApiEndpoint: string = `${this.baseApi}${downloadSweepApiResource}`;

      const downloadSweepApiResponse: AxiosResponse<string[]> =
        await apiClient.post<string[]>(downloadApiEndpoint, { sweepIds });

      console.log(`[SweepService][download sweep report]`, {
        downloadSweepApiResponse,
      });

      return downloadSweepApiResponse;
    } catch (error: unknown) {
      console.error(`[SweepService][download sweep report][ERROR]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    } finally {
      console.log(`[SweepService][download sweep report] finally block`);
    }
  }
}
